import { createContext, useContext, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface FilterState {
  branch: string | null;
  // Passout/graduation year (e.g. "2026"), not an academic-year label like
  // "2025-26" — a single academic-year label is shared by multiple passout
  // cohorts (TY students mid-program vs. finalists graduating that year),
  // which made the old academic-year filter under/over-count. Graduation
  // year is unambiguous.
  graduationYear: string | null;
  activityType: 'all' | 'internship' | 'achievement' | 'freelancing';
  snapshotDate: string | null;
  drilldownMetric: string | null;
}

interface FilterContextType {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  updateFilter: (key: keyof FilterState, value: any) => void;
  resetFilters: () => void;
}

const defaultState: FilterState = {
  branch: null,
  graduationYear: null,
  activityType: 'all',
  snapshotDate: null,
  drilldownMetric: null,
};

// URL query param each filter key reads/writes. Short names keep dashboard
// URLs (e.g. /dashboard/students?branch=CE&year=2027) readable and
// shareable — this is the whole point of the URL-sync: refresh, deep
// links, and browser back/forward all fall out of it for free.
const PARAM_KEYS: Record<keyof FilterState, string> = {
  branch: 'branch',
  graduationYear: 'year',
  activityType: 'activity',
  snapshotDate: 'snapshot',
  drilldownMetric: 'drilldown',
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

function readFilters(searchParams: URLSearchParams): FilterState {
  return {
    branch: searchParams.get(PARAM_KEYS.branch),
    graduationYear: searchParams.get(PARAM_KEYS.graduationYear),
    activityType:
      (searchParams.get(PARAM_KEYS.activityType) as FilterState['activityType']) || 'all',
    snapshotDate: searchParams.get(PARAM_KEYS.snapshotDate),
    drilldownMetric: searchParams.get(PARAM_KEYS.drilldownMetric),
  };
}

function writeFilters(prev: URLSearchParams, next: FilterState): URLSearchParams {
  const params = new URLSearchParams(prev);
  (Object.keys(PARAM_KEYS) as (keyof FilterState)[]).forEach((key) => {
    const value = next[key];
    const paramKey = PARAM_KEYS[key];
    const isDefault = value === null || value === undefined || value === '' ||
      (key === 'activityType' && value === 'all');
    if (isDefault) {
      params.delete(paramKey);
    } else {
      params.set(paramKey, String(value));
    }
  });
  return params;
}

// FilterContext is now a thin cache/parser around the URL rather than the
// source of truth — the URL is. This makes every dashboard view a real,
// shareable, refresh-safe deep link (e.g. /dashboard/students?branch=CE).
// `replace: true` on every write avoids flooding browser history with one
// entry per filter tweak; normal tab-to-tab navigation (via <Link>/<NavLink>)
// still pushes history entries, so back/forward moves between sections as
// expected.
export function FilterProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const updateFilter = useCallback(
    (key: keyof FilterState, value: any) => {
      setSearchParams((prev) => writeFilters(prev, { ...readFilters(prev), [key]: value }), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    setSearchParams((prev) => writeFilters(prev, defaultState), { replace: true });
  }, [setSearchParams]);

  // Bulk setter kept only for interface compatibility — nothing in the app
  // calls it today, but it's part of the exported contract.
  const setFilters = useCallback<React.Dispatch<React.SetStateAction<FilterState>>>(
    (update) => {
      setSearchParams(
        (prev) => {
          const current = readFilters(prev);
          const next = typeof update === 'function' ? (update as (p: FilterState) => FilterState)(current) : update;
          return writeFilters(prev, next);
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <FilterContext.Provider value={{ filters, setFilters, updateFilter, resetFilters }}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
